import {useCallback, useMemo, useState} from 'react'
import {WithTranslation, withTranslation} from 'react-i18next'
import Skeleton from 'react-loading-skeleton'
import {useNavigate} from 'react-router-dom'
import classNames from 'classnames'

import {ReactComponent as Download} from '../../../../assets/icons/download.svg'
import {ReactComponent as DownloadPurple} from '../../../../assets/icons/download-purple.svg'
import {ReactComponent as UserSuccess} from '../../../../assets/icons/user-success.svg'
import Button from '../../../../components/Button/Button'
import Card from '../../../../components/Card/Card'
import PercentageCircle from '../../../../components/ItemCard/components/PercentageCircle/PercentageCircle'
import Modal from '../../../../components/Modal/Modal'
import ProgressBar from '../../../../components/ProgressBar/ProgressBar'
import {PATHS} from '../../../../constants/paths'
import useBreakpoint from '../../../../hooks/useBreakpoint'
import {ICharacteristic} from '../../../../services/interfaces/ICharacteristics'
import {BUSINESS_MODEL_TYPE, TYPOLOGY_TYPE} from '../../../../services/interfaces/IProject'
import {
  getInvestmentStorage,
  saveInvestmentStorage,
} from '../../../../store/localStorage/investment'
import {useCustomerSlice} from '../../../../store/slices/customer'
import {useProjectSlice} from '../../../../store/slices/project'
import {useStepperSlice} from '../../../../store/slices/stepper'
import Config from '../../../../utils/config'
import {getCharacteristicTranslation} from '../../../../utils/getCharacteristicTranslation'
import Tokenomic from '../Tokenomic/Tokenomic'
import TokenomicInfo from '../TokenomicInfo/TokenomicInfo'

import styles from './Info.module.scss'

interface IInfoProps extends WithTranslation {
  className?: string
  straightCorners?: boolean
}

const Info = ({className, t, straightCorners, i18n}: IInfoProps) => {
  const navigate = useNavigate()
  const [kycModalVisible, showKycModal] = useState<boolean>(false)
  const {
    customer_info: {identity_verification_status},
  } = useCustomerSlice()
  const {steps, setActiveStep} = useStepperSlice()
  const {
    id,
    description,
    description_en,
    characteristics,
    tokenomic: {investment_status},
    whitepaper_url,
    brochure_url,
    agreement_url,
    business_model,
    typology,
    development_progress,
  } = useProjectSlice()
  const breakpoint = useBreakpoint()

  const investAction = useCallback(() => {
    saveInvestmentStorage({
      projectId: id,
      activeStepNumber: undefined,
      activeSubStep: undefined,
      paymentIntentClientSecret: undefined,
      paymentMethodTypeSelected: undefined,
      isPaymentSuccessful: undefined,
    })
    setActiveStep(steps[0])
    identity_verification_status === 'approved' || !Config.isKycEnabled
      ? navigate(`${PATHS.INVESTMENT}/${id}`)
      : showKycModal(true)
  }, [
    id,
    identity_verification_status,
    getInvestmentStorage,
    saveInvestmentStorage,
    kycModalVisible,
    setActiveStep,
  ])

  const projectFeaturedCharacteristics = useMemo<ICharacteristic[]>(
    () =>
      (!!characteristics?.length &&
        characteristics.filter(characteristic => characteristic.type === 'PROJECT_FEATURED')) ||
      [],
    [characteristics],
  )

  return (
    <>
      <div className={classNames(styles.container, className)}>
        <div className={styles.firstColumn}>
          <div className={styles.infoHeader}>
            <div className={styles.title}>
              <span>{t('item.typology_title')}:</span>
              <span>{t(`item.typology.${TYPOLOGY_TYPE[typology]}`)}</span>
            </div>
            <div className={styles.subtitle}>{t('item.featuredInformation')}</div>
          </div>
          <TokenomicInfo
            // className={classNames(styles.card)}
            withShadow={breakpoint !== 'm' && breakpoint !== 's' && breakpoint !== 'xs'}
            straightCorners={straightCorners}
          />

          <div className={styles.infoDescription}>
            <span>{t('item.propertyFeatures')}</span>
            <div className={styles.description}>
              {(i18n.language === 'en' && description_en ? description_en : description) || (
                <Skeleton width={210} />
              )}
            </div>
          </div>

          {!!development_progress && (
            <>
              <Card
                className={styles.progressCard}
                withShadow={breakpoint !== 'm' && breakpoint !== 's' && breakpoint !== 'xs'}
                straightCorners={straightCorners}>
                <div className={styles.header}>
                  <span>{t('item.developmentProgress')}</span>
                  <span>{t('item.excavations')}</span>
                </div>
                <ProgressBar completed={+development_progress} completedColor="primary" />
              </Card>
              <div className={styles.divider} />
            </>
          )}

          {!!characteristics?.length && !!projectFeaturedCharacteristics.length && (
            <Card
              className={classNames(styles.card)}
              withShadow={breakpoint !== 'm' && breakpoint !== 's' && breakpoint !== 'xs'}
              straightCorners={straightCorners}>
              <div className={styles.rows}>
                {characteristics
                  .filter(characteristic => characteristic.type === 'PROJECT_FEATURED')
                  .map(projectCharacteristic => (
                    <div key={projectCharacteristic.id} className={styles.featuredRow}>
                      <div
                        className={styles.icon}
                        style={{backgroundImage: `url(${projectCharacteristic.image_url})`}}
                      />
                      <div className={styles.text}>
                        <span>{t(`item.projectCharacteristic.${projectCharacteristic.name}`)}</span>
                        <span>
                          {getCharacteristicTranslation(projectCharacteristic, i18n.language)}
                        </span>
                      </div>
                    </div>
                  ))}
              </div>
              <div className={styles.divider} />
            </Card>
          )}

          <div className={styles.buttonRow}>
            {brochure_url && (
              <a
                className={styles.linkButton}
                href={brochure_url}
                target="_blank"
                rel="noopener noreferrer">
                <Button className={styles.button} filledColor="secondary">
                  <Download />
                  {t('downloadBrochure')}
                </Button>
              </a>
            )}
            {agreement_url && (
              <a
                className={styles.linkButton}
                href={agreement_url}
                target="_blank"
                rel="noopener noreferrer">
                <Button className={styles.button} styledType="outline" outlinedColor="secondary">
                  <DownloadPurple className={styles.icon} />
                  {t('downloadAgreement')}
                </Button>
              </a>
            )}
            {whitepaper_url && (
              <a
                className={styles.linkButton}
                href={whitepaper_url}
                target="_blank"
                rel="noopener noreferrer">
                <Button className={styles.button} styledType="outline" outlinedColor="secondary">
                  <DownloadPurple className={styles.icon} />
                  {t('downloadWhitePaper')}
                </Button>
              </a>
            )}
          </div>
        </div>
        <div className={styles.secondColumn}>
          <div className={styles.infoHeader}>
            <div className={styles.column}>
              <div className={styles.title}>
                <span>{t('item.businessModel_title')}:</span>
                <span> {t(`item.businessModel.${BUSINESS_MODEL_TYPE[business_model]}`)}</span>
              </div>
              <div className={styles.subtitle}>{t('item.financialInformation')}</div>
            </div>
            <PercentageCircle
              percentage={investment_status}
              className={styles.percentage}
              borderColor="primary"
            />
          </div>
          <Tokenomic investAction={investAction} straightCorners={straightCorners} />
        </div>
      </div>

      {Config.isKycEnabled && (
        <Modal
          visible={kycModalVisible}
          onClose={() => showKycModal(false)}
          containerClassName={styles.kycModal}
          icon={<UserSuccess />}
          title={'Para invertir necesitamos su KYC'}
          subtitle={'Siga los siguientes pasos en orden para continuar'}
          button={{
            label: t('continue'),
            onClick: () => navigate(`${PATHS.INVESTMENT}/${id}`),
          }}
        />
      )}
    </>
  )
}

export default withTranslation()(Info)
